import React, { createContext, useState, useEffect, useContext } from 'react';
import { Product, CartItem } from '../types';

// ... existing code ...
interface CartContextType {
    cart: CartItem[];
    addToCart: (product: Product, quantity?: number) => void;
    removeFromCart: (itemKey: number | string) => void;
    decreaseQuantity: (itemKey: number | string) => void;
    clearCart: () => void;
    totalItems: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const createCartKey = (product: Product) => {
    const selections = product.promo_selecciones || [];
    if (!selections.length) return String(product.idProductos);

    const fingerprint = selections
        .map(selection => `${selection.id_grupo || 0}:${selection.id_producto}:${selection.cantidad}`)
        .sort()
        .join('|');

    return `${product.idProductos}:${fingerprint}`;
};

const matchesItemKey = (item: CartItem, key: number | string) => {
    const normalized = String(key);
    return item.cartKey === normalized || String(item.idProductos) === normalized;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, setCart] = useState<CartItem[]>(() => {
        const savedCart = localStorage.getItem('cart');
        if (!savedCart) return [];
        try {
            const parsed = JSON.parse(savedCart) as CartItem[];
            return parsed.map(item => ({ ...item, cartKey: item.cartKey || createCartKey(item) }));
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (product: Product, quantity = 1) => {
        if (product.stock_total !== null && product.stock_total !== undefined && product.stock_total <= 0) {
            return;
        }
        const cartKey = product.cartKey || createCartKey(product);
        setCart(currentCart => {
            const existingItem = currentCart.find(item => item.cartKey === cartKey);
            if (existingItem) {
                return currentCart.map(item =>
                    item.cartKey === cartKey
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...currentCart, { ...product, cartKey, quantity }];
        });
    };

    const decreaseQuantity = (itemKey: number | string) => {
        setCart(currentCart => {
            const existingItem = currentCart.find(item => matchesItemKey(item, itemKey));
            if (existingItem?.quantity === 1) {
                return currentCart.filter(item => !matchesItemKey(item, itemKey));
            }
            return currentCart.map(item =>
                matchesItemKey(item, itemKey)
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            );
        });
    };

    const removeFromCart = (itemKey: number | string) => {
        setCart(currentCart => currentCart.filter(item => !matchesItemKey(item, itemKey)));
    };

    const clearCart = () => {
        setCart([]);
    };

    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, decreaseQuantity, clearCart, totalItems }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
